"use client";

import * as React from "react";
import * as RSelect from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "../cn";
import type { SelectOption } from "../select";

export type FilterSelectProps = {
  value: string;
  onValueChange: (v: string) => void;
  options: SelectOption[];
  /** Label shown when no value selected (acts as the "all" option). */
  placeholder: string;
  className?: string;
  width?: string;
  /** Optional leading icon. */
  icon?: React.ReactNode;
};

const ALL_VALUE = "__all__";

export const FilterSelect = React.forwardRef<
  HTMLButtonElement,
  FilterSelectProps
>(
  (
    {
      value,
      onValueChange,
      options,
      placeholder,
      className,
      width = "w-44",
      icon,
    },
    ref,
  ) => {
    const internal = value === "" ? ALL_VALUE : value;
    return (
      <RSelect.Root
        value={internal}
        onValueChange={(v) => onValueChange(v === ALL_VALUE ? "" : v)}
      >
        <RSelect.Trigger
          ref={ref}
          className={cn(
            "group h-10 rounded-[var(--radius-sm)] border-[1.5px]",
            "px-3 text-sm font-satoshi font-medium text-ink",
            "bg-mist border-smoke outline-none transition-all cursor-pointer",
            "focus:border-lime focus:bg-paper",
            "data-[state=open]:border-lime data-[state=open]:bg-paper",
            "flex items-center justify-between gap-2",
            value !== "" && "border-lime/60 bg-paper",
            width,
            className,
          )}
        >
          <div className="flex items-center gap-1.5 truncate">
            {icon ? <span className="text-ash">{icon}</span> : null}
            <span className={cn("truncate", value === "" && "text-ash")}>
              <RSelect.Value placeholder={placeholder} />
            </span>
          </div>
          <RSelect.Icon className="shrink-0 text-ash transition-transform duration-200 group-data-[state=open]:rotate-180">
            <ChevronDown size={16} strokeWidth={2} />
          </RSelect.Icon>
        </RSelect.Trigger>
        <RSelect.Portal>
          <RSelect.Content
            position="popper"
            sideOffset={6}
            className={cn(
              "z-50 min-w-[var(--radix-select-trigger-width)] overflow-hidden",
              "rounded-[var(--radius-md)] border border-smoke bg-paper",
              "shadow-[0_8px_24px_-4px_rgba(0,0,0,0.10),0_2px_8px_-2px_rgba(0,0,0,0.06)]",
              "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
              "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
              "origin-top duration-150",
            )}
          >
            <RSelect.Viewport className="p-1.5 max-h-72">
              <RSelect.Item
                value={ALL_VALUE}
                className={cn(
                  "relative flex items-center h-9 px-3 rounded-[var(--radius-sm)]",
                  "text-sm font-medium text-ash outline-none cursor-pointer select-none",
                  "transition-colors duration-100",
                  "data-[highlighted]:bg-mist",
                  "data-[state=checked]:text-ember",
                )}
              >
                <RSelect.ItemText>{placeholder}</RSelect.ItemText>
                <RSelect.ItemIndicator className="ml-auto pl-2">
                  <Check size={13} strokeWidth={2.5} className="text-ember" />
                </RSelect.ItemIndicator>
              </RSelect.Item>
              {options.map((opt) => (
                <RSelect.Item
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.disabled}
                  className={cn(
                    "relative flex items-center h-9 px-3 rounded-[var(--radius-sm)]",
                    "text-sm font-medium text-ink outline-none cursor-pointer select-none",
                    "transition-colors duration-100",
                    "data-[highlighted]:bg-mist",
                    "data-[state=checked]:text-ember",
                    "data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed",
                  )}
                >
                  <RSelect.ItemText>{opt.label}</RSelect.ItemText>
                  <RSelect.ItemIndicator className="ml-auto pl-2">
                    <Check size={13} strokeWidth={2.5} className="text-ember" />
                  </RSelect.ItemIndicator>
                </RSelect.Item>
              ))}
            </RSelect.Viewport>
          </RSelect.Content>
        </RSelect.Portal>
      </RSelect.Root>
    );
  },
);
FilterSelect.displayName = "FilterSelect";
